import { useEffect, useMemo, useRef, useState } from 'react';
import { createTask, uploadTaskAttachment, type TaskProject, type TaskUser, type TaskPriority, type TaskRecurrence } from '../api/tasks';
import type { SpaceListItem } from '../api/auth';
import { useFeather } from '../shell/useFeather';
import { SpaceIcon, AssigneePicker, MAX_ATTACH_BYTES, formatSize, fileToBase64 } from './ukoly-shared';
import { linkifyContentEditable } from './linkify';
import { insertLinkDialog } from '../shell/dialogs';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';

const PRIORITY_OPTIONS: TaskPriority[] = ['low', 'normal', 'high', 'urgent'];
const PRIORITY_LABEL: Record<TaskPriority, string> = { low: 'Low', normal: 'Medium', high: 'High', urgent: 'Urgent' };
const RECURRENCE_OPTIONS: TaskRecurrence[] = ['none', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
const RECURRENCE_LABEL: Record<TaskRecurrence, string> = {
  none: 'Neopakovat', daily: 'Denně', weekly: 'Týdně', monthly: 'Měsíčně', quarterly: 'Kvartálně', yearly: 'Ročně',
};


interface Props {
  spaces: SpaceListItem[];
  projects: TaskProject[];
  users: TaskUser[];
  defaultSpaceId: number | null;
  defaultProjectId: number | null;
  onClose: () => void;
  onCreated: (taskId: number) => void;
}

export function UkolyCreate({ spaces, projects, users, defaultSpaceId, defaultProjectId, onClose, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [spaceId, setSpaceId] = useState<number | null>(defaultSpaceId ?? spaces[0]?.id ?? null);
  const [projectId, setProjectId] = useState<number | null>(defaultProjectId);
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueAt, setDueAt] = useState('');
  const [recurrence, setRecurrence] = useState<TaskRecurrence>('none');
  const [assigneeUserId, setAssigneeUserId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  useFeather([spaces.length, pendingFiles.length, showMoreFields]);

  // Bez tohoto nastavení Chrome v prázdném contentEditable obaluje enter nekonzistentně a víceřádkový popis se po uložení slije do jednoho odstavce.
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'div'); } catch { /* no-op */ }
  }, []);

  function addFiles(files: FileList | File[]) {
    const accepted: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACH_BYTES) {
        setError(`Soubor „${f.name}" je příliš velký (${formatSize(f.size)}). Maximum je 25 MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setPendingFiles((cur) => [...cur, ...accepted]);
  }

  // Drag&drop na úrovni dokumentu (capture), stejně jako v detailu úkolu:
  // skutečný OS drop padne na libovolný list pod kurzorem, ne na panel.
  // preventDefault musí běžet bezpodmínečně, jinak prohlížeč drop odmítne.
  useEffect(() => {
    let dragCounter = 0;
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounter++;
      setDragOver(true);
    };
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter <= 0) { dragCounter = 0; setDragOver(false); }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (files && files.length) addFiles(files);
    };
    document.addEventListener('dragenter', onDragEnter, true);
    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('dragleave', onDragLeave, true);
    document.addEventListener('drop', onDrop, true);
    return () => {
      document.removeEventListener('dragenter', onDragEnter, true);
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('dragleave', onDragLeave, true);
      document.removeEventListener('drop', onDrop, true);
    };
  }, []);

  // The panel can open while Spaces are still loading. The native select then
  // displays its first option even though the state is still null.
  const selectedSpaceId = spaces.some((space) => space.id === spaceId)
    ? spaceId
    : spaces.find((space) => space.id === defaultSpaceId)?.id ?? spaces[0]?.id ?? null;
  const spaceProjects = useMemo(
    () => projects.filter((p) => selectedSpaceId != null && p.spaceId === selectedSpaceId),
    [projects, selectedSpaceId],
  );

  async function submit() {
    const t = title.trim();
    if (saving) return;
    if (!t) { setError('Vyplň název úkolu.'); return; }
    if (selectedSpaceId == null) { setError('Vyber Space úkolu.'); return; }
    const editor = descriptionRef.current;
    if (editor) linkifyContentEditable(editor);
    const descriptionHtml = editor?.innerHTML.trim() ?? '';
    setError(null);
    setSaving(true);
    try {
      const created = await createTask({
        primarySpaceId: selectedSpaceId,
        title: t,
        descriptionHtml,
        projectId: projectId ?? undefined,
        priority,
        recurrence,
        dueAt: dueAt ? new Date(dueAt + 'T00:00:00').toISOString() : undefined,
        assigneeUserId: assigneeUserId ?? undefined,
      });
      // Přílohy se vážou na ID úkolu, které vznikne až teď. Úkol je už uložený,
      // takže případné selhání uploadu hlásíme, ale úkol nezahazujeme.
      const failed: string[] = [];
      for (const f of pendingFiles) {
        try {
          const contentBase64 = await fileToBase64(f);
          await uploadTaskAttachment(created.id, {
            originalFilename: f.name,
            contentBase64,
            mimeType: f.type || null,
          });
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) {
        setError(`Úkol byl vytvořen, ale tyto přílohy se nenahrály: ${failed.join(', ')}. Zkus je přidat v detailu úkolu.`);
        setSaving(false);
        return;
      }
      onCreated(created.id);
    } catch (e) {
      setError(String(e));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="asn-detail-overlay open" onClick={onClose}></div>
      <aside className={`asn-detail-pane asn-arc-pane p1-arc-scope open${dragOver ? ' dragging' : ''}`}>
        <div className="asn-detail-toolbar">
          <strong className="asn-detail-toolbar-title">Nový úkol</strong>
          <span className="asn-detail-toolbar-spacer"></span>
          <button type="button" className="asn-icon-btn" title="Zavřít" onClick={onClose}>
            <span className="feather-icon"><i data-feather="x"></i></span>
          </button>
        </div>
        <div className="asn-detail-body">
          <input
            className="asn-detail-title"
            autoFocus
            placeholder="např. Poslat fakturu klientovi"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); descriptionRef.current?.focus(); } }}
          />
          <div className="asn-detail-grid asn-task-meta">
            <div className="asn-task-meta-primary" aria-label="Hlavní nastavení úkolu">
              <div className="asn-task-meta-field asn-task-meta-field-assignee">
                <div className="label"><span className="feather-icon"><i data-feather="user"></i></span>Přiřazeno</div>
                <AssigneePicker users={users} value={assigneeUserId} onChange={setAssigneeUserId} />
              </div>
              <div className="asn-task-meta-field asn-task-meta-field-due">
                <div className="label"><span className="feather-icon"><i data-feather="calendar"></i></span>Termín</div>
                <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
              </div>
              <div className="asn-task-meta-field asn-task-meta-field-category">
                <div className="label"><span className="feather-icon"><i data-feather="folder"></i></span>Kategorie</div>
                <CompactSelect className="asn-arc-select" value={String(projectId ?? '')} onChange={(next) => setProjectId(next ? Number(next) : null)} ariaLabel="Kategorie" options={[{ value: '', label: 'Bez kategorie' }, ...spaceProjects.map((p) => ({ value: String(p.id), label: p.name }))]} />
              </div>
            </div>
            <button
              type="button"
              className={`asn-task-meta-more${showMoreFields ? ' open' : ''}`}
              aria-expanded={showMoreFields}
              aria-controls="new-task-more-fields"
              onClick={() => setShowMoreFields((open) => !open)}
            >
              <span className="asn-task-meta-more-copy">
                <strong>Další nastavení</strong>
                <small>{PRIORITY_LABEL[priority]} · {RECURRENCE_LABEL[recurrence]} · {spaces.find((s) => s.id === selectedSpaceId)?.name ?? 'Space'}</small>
              </span>
              <svg className="asn-task-meta-more-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            <div id="new-task-more-fields" className={`asn-task-meta-secondary${showMoreFields ? ' open' : ''}`}>
              <div className="asn-task-meta-field asn-task-meta-field-priority">
                <div className="label"><span className="feather-icon"><i data-feather="flag"></i></span>Priorita</div>
                <CompactSelect className="asn-arc-select" value={priority} onChange={(next) => setPriority(next as TaskPriority)} ariaLabel="Priorita" options={PRIORITY_OPTIONS.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))} />
              </div>
              <div className="asn-task-meta-field asn-task-meta-field-recurrence">
                <div className="label"><span className="feather-icon"><i data-feather="repeat"></i></span>Opakovat</div>
                <CompactSelect className="asn-arc-select" value={recurrence} onChange={(next) => setRecurrence(next as TaskRecurrence)} ariaLabel="Opakovat" options={RECURRENCE_OPTIONS.map((r) => ({ value: r, label: RECURRENCE_LABEL[r] }))} />
              </div>
              <div className="asn-task-meta-field asn-task-meta-field-space">
                <div className="label"><span className="feather-icon"><i data-feather="grid"></i></span>Space</div>
                <div className="asn-space-field">
                  <SpaceIcon space={spaces.find((s) => s.id === selectedSpaceId)} size={20} />
                  <CompactSelect className="asn-arc-select" value={String(selectedSpaceId ?? '')} onChange={(next) => { setSpaceId(next ? Number(next) : null); setProjectId(null); }} ariaLabel="Space" options={spaces.length ? spaces.map((s) => ({ value: String(s.id), label: s.name })) : [{ value: '', label: 'Načítám Spaces…' }]} />
                </div>
              </div>
            </div>
          </div>
          <div className="asn-detail-desc-label">Popis</div>
          <div className="asn-desc-wrap">
            <div
              ref={descriptionRef}
              className="asn-detail-desc"
              contentEditable
              role="textbox"
              aria-label="Popis úkolu"
              aria-multiline="true"
              suppressContentEditableWarning
              onPaste={(e) => {
                const el = e.currentTarget;
                setTimeout(() => linkifyContentEditable(el), 0);
              }}
            />
            <div className="asn-detail-desc-toolbar">
              <button type="button" title="Tučně (Cmd+B)" onMouseDown={(e) => { e.preventDefault(); descriptionRef.current?.focus(); document.execCommand('bold'); }}><strong>B</strong></button>
              <button type="button" title="Kurzíva (Cmd+I)" onMouseDown={(e) => { e.preventDefault(); descriptionRef.current?.focus(); document.execCommand('italic'); }}><em>I</em></button>
              <button type="button" title="Odkaz (Cmd+K)" onMouseDown={(e) => { e.preventDefault(); descriptionRef.current?.focus(); void insertLinkDialog(); }}><span className="feather-icon"><i data-feather="link"></i></span></button>
            </div>
          </div>
          <div className="asn-detail-desc-label" style={{ marginTop: 18 }}>Přílohy</div>
          <label className="asn-attach-drop" title="Nebo přetáhni soubor kamkoli do panelu">
            <span className="feather-icon"><i data-feather="paperclip"></i></span>
            <span><strong>Přidat přílohu</strong> - klikni nebo přetáhni soubor kamkoli do panelu</span>
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
            />
          </label>
          {pendingFiles.length > 0 && (
            <div className="asn-attach-list">
              {pendingFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} className="asn-attach-item" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <div className="asn-attach-icon">
                      <span className="feather-icon"><i data-feather="paperclip"></i></span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="asn-attach-name">{f.name}</div>
                      <div className="asn-attach-meta">{formatSize(f.size)} · nahraje se po vytvoření</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="asn-attach-remove"
                    title="Odebrat přílohu"
                    onClick={() => setPendingFiles((cur) => cur.filter((_, idx) => idx !== i))}
                    style={{
                      position: 'absolute', top: 4, right: 4, width: 20, height: 20,
                      border: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.06)',
                      color: '#6b7280', cursor: 'pointer', fontSize: 14, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button type="button" variant="primary" size="sm" onClick={() => void submit()} disabled={saving}>
              {saving ? (pendingFiles.length ? 'Vytvářím a nahrávám přílohy…' : 'Vytvářím…') : 'Vytvořit úkol'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Zrušit</Button>
          </div>
        </div>
      </aside>
    </>
  );
}
